import { useState, useEffect } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import { fetchData, updateData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar, Button, CircularProgress } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';

const UserEdit = () => {
    useSessionValidation();

    const { id } = useParams();
    const history = useHistory();
    const { isAuthenticated, companyId, userId, logout, token } = useAuth();

    const [isLoading, setIsLoading]       = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage,   setErrorMessage]   = useState('');

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) { logout(); }
    }, [isAuthenticated, companyId]);

    const [formData, setFormData] = useState({
        FirstName: '', LastName: '', UserName: '',
        Email: '', ContactNumber: ''
    });

    useEffect(() => {
        fetchData('UserApi', `/UserDetails/${id}`, token)
            .then(r => { if (r.responseCode === '1111') setFormData(r.response_data); })
            .catch(err => console.error('Error fetching User Details:', err));
    }, [id]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'FirstName' || name === 'LastName') {
                const fn = name === 'FirstName' ? value : prev.FirstName;
                const ln = name === 'LastName'  ? value : prev.LastName;
                updated.UserName = `${fn.toLowerCase()}_${ln.toLowerCase()}`.slice(0, 15);
            }
            return updated;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);
        updateData('UserApi', `${id}`, { ...formData, UpdatedBy: userId }, token)
            .then(() => {
                setSuccessMessage('User updated successfully!');
                setTimeout(() => history.push('/AuthConsole'), 1000);
            })
            .catch(() => { setErrorMessage('Failed to update user.'); setIsLoading(false); });
    };

    // ── Shared styles ──
    const labelStyle = {
        display: 'block', fontSize: '10px', fontWeight: '700',
        color: '#718096', marginBottom: '3px',
        textTransform: 'uppercase', letterSpacing: '0.4px',
    };

    const inputStyle = (readOnly) => ({
        width: '100%', padding: '6px 10px', fontSize: '12px',
        borderRadius: '7px', border: '1px solid #d0d7e3',
        background: readOnly ? '#f8f9fa' : 'white',
        outline: 'none', fontFamily: 'Times New Roman',
        boxSizing: 'border-box', color: '#2d3748',
        cursor: readOnly ? 'not-allowed' : 'text',
        transition: 'border-color 0.2s',
    });

    const sectionHeader = (icon, title) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px', marginBottom: '10px', paddingBottom: '7px', borderBottom: '1px solid #e2e8f0' }}>
            <div style={{ background: 'linear-gradient(135deg,#2C3E6E,#3d5491)', borderRadius: '6px', padding: '5px', display: 'flex' }}>
                {icon}
            </div>
            <span style={{ fontSize: '11px', fontWeight: '700', color: '#2C3E6E', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{title}</span>
        </div>
    );

    return (
        <div style={{ minWidth: '100%', background: '#f0f2f5', padding: '10px', fontFamily: 'Times New Roman' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

                {/* ── Gradient Header — single compact row ── */}
                <div style={{
                    background: 'linear-gradient(135deg, #2C3E6E 0%, #3d5491 100%)',
                    borderRadius: '12px', padding: '10px 16px', marginBottom: '10px',
                    color: 'white', boxShadow: '0 4px 12px rgba(44,62,110,0.25)',
                    display: 'flex', alignItems: 'center', gap: '12px',
                }}>
                    {/* Avatar with initials */}
                    <div style={{
                        width: '38px', height: '38px', borderRadius: '50%', flexShrink: 0,
                        background: 'rgba(255,255,255,0.2)', border: '2px solid rgba(255,255,255,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '13px', fontWeight: '700',
                    }}>
                        {(formData.FirstName?.[0] || '').toUpperCase()}{(formData.LastName?.[0] || '').toUpperCase()}
                    </div>

                    {/* Title + subtitle */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: '14px', fontWeight: '700', letterSpacing: '0.2px' }}>
                            Edit User{formData.FirstName ? ` — ${formData.FirstName} ${formData.LastName}` : ''}
                        </div>
                        <div style={{ fontSize: '11px', opacity: 0.82, marginTop: '1px' }}>
                            {formData.UserName && <span>@{formData.UserName}</span>}
                            {formData.Email    && <span style={{ marginLeft: '10px' }}>{formData.Email}</span>}
                        </div>
                    </div>

                    {/* Category pills */}
                    <div style={{ display: 'flex', gap: '5px', flexShrink: 0 }}>
                        {[
                            { icon: '👤', label: 'Personal Info' },
                            { icon: '📞', label: 'Contact Details' },
                        ].map(s => (
                            <span key={s.label} style={{
                                background: 'rgba(255,255,255,0.15)', padding: '3px 9px',
                                borderRadius: '5px', fontSize: '11px', fontWeight: '600',
                                display: 'inline-flex', alignItems: 'center', gap: '4px', whiteSpace: 'nowrap',
                            }}>
                                {s.icon} {s.label}
                            </span>
                        ))}
                    </div>

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                        <Button
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={isLoading}
                            startIcon={isLoading ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: '13px' }} />}
                            sx={{
                                background: 'rgba(255,255,255,0.22)', color: 'white',
                                padding: '4px 12px', borderRadius: '7px', fontSize: '11px',
                                fontWeight: '700', textTransform: 'none', minHeight: '28px',
                                boxShadow: 'none',
                                '&:hover': { background: 'rgba(255,255,255,0.32)' },
                                '&:disabled': { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' },
                            }}
                        >
                            {isLoading ? 'Saving...' : 'Update User'}
                        </Button>
                        <Button
                            variant="outlined"
                            component={Link}
                            to="/AuthConsole"
                            startIcon={<ArrowBackIcon sx={{ fontSize: '13px' }} />}
                            sx={{
                                borderColor: 'rgba(255,255,255,0.5)', color: 'white',
                                padding: '4px 12px', borderRadius: '7px', fontSize: '11px',
                                fontWeight: '700', textTransform: 'none', minHeight: '28px',
                                '&:hover': { borderColor: 'white', background: 'rgba(255,255,255,0.1)' }
                            }}
                        >
                            Back
                        </Button>
                    </div>
                </div>

                {/* ── Single wide card — all fields in one 5-column row ── */}
                <div style={{ background: 'white', borderRadius: '10px', padding: '14px 16px', border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                    {sectionHeader(<PersonIcon style={{ fontSize: '14px', color: 'white' }} />, 'User Information')}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '12px' }}>
                        <div>
                            <label style={labelStyle}>First Name *</label>
                            <input name="FirstName" value={formData.FirstName} onChange={handleChange} type="text" placeholder="Enter first name" maxLength={30} style={inputStyle(false)} />
                        </div>
                        <div>
                            <label style={labelStyle}>Last Name *</label>
                            <input name="LastName" value={formData.LastName} onChange={handleChange} type="text" placeholder="Enter last name" maxLength={30} style={inputStyle(false)} />
                        </div>
                        <div>
                            <label style={labelStyle}>Username</label>
                            <input name="UserName" value={formData.UserName} readOnly type="text" style={inputStyle(true)} />
                        </div>
                        <div>
                            <label style={labelStyle}>Contact Number *</label>
                            <input name="ContactNumber" value={formData.ContactNumber} onChange={handleChange} type="text" placeholder="Enter contact number" maxLength={15} style={inputStyle(false)} />
                        </div>
                        <div>
                            <label style={labelStyle}>Email Address *</label>
                            <input name="Email" value={formData.Email} onChange={handleChange} type="email" placeholder="user@example.com" maxLength={40} style={inputStyle(false)} />
                        </div>
                    </div>
                </div>

            </div>

            <Snackbar open={!!errorMessage || !!successMessage} autoHideDuration={4000} onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
                <Alert onClose={() => { setErrorMessage(''); setSuccessMessage(''); }} severity={errorMessage ? 'error' : 'success'} sx={{ fontSize: '12px' }}>
                    {errorMessage || successMessage}
                </Alert>
            </Snackbar>
        </div>
    );
};

export default UserEdit;
